import { Message, MessagePrev } from "../types";
import { getLessonTitleFromAny } from "./messageHelpers";

export type ContentKind = "lesson" | "crash_course" | "message";

type IdentifyOptions = {
  lessonIds?: Set<string>;
  crashCourseIds?: Set<string>;
  favoriteTabLabelsCache?: Record<string, string[]>;
};

const normalizeBadgeTitle = (raw: string): string =>
  raw.trim().toLowerCase().replace(/\s+/g, " ");

const tabLabelsFromItem = (
  item: MessagePrev,
  tabLabelsCache?: Record<string, string[]>,
): string[] => {
  if (item.tabbed_content?.length) {
    return item.tabbed_content
      .map((t) => t.tab_label)
      .filter((x): x is string => typeof x === "string" && x.trim().length > 0);
  }
  return tabLabelsCache?.[item.message_id] ?? [];
};

// Lesson tab chrome: classic quiz/feedback flow OR packaged-lesson tabs
// (Audio Overview / Slide Show / Your Progress — not used on generic pharma/evidence tabs).
export const isLikelyLessonTabbed = (
  item: MessagePrev,
  tabLabelsCache?: Record<string, string[]>,
): boolean => {
  const labels = tabLabelsFromItem(item, tabLabelsCache);
  if (labels.length === 0) return false;
  const low = labels.map((l) => l.toLowerCase());
  const hasQuiz = low.some((l) => l.includes("quiz"));
  const hasFeedback = low.some((l) => l.includes("feedback"));
  if (hasQuiz && hasFeedback) return true;

  const hasAudioOverview = low.some((l) => l.includes("audio overview"));
  const hasSlideShow = low.some((l) => l.includes("slide show"));
  if (hasAudioOverview || hasSlideShow) return true;

  const hasYourProgress = low.some((l) => l.includes("your progress"));
  const hasTopicTab = low.some((l) => {
    const t = l.trim();
    return t === "topic" || t.startsWith("topic ");
  });
  if (hasYourProgress && hasTopicTab) return true;

  return false;
};

export const isLikelyCrashCourseTitle = (item: MessagePrev): boolean => {
  const t = normalizeBadgeTitle(getLessonTitleFromAny(item));
  return t === "crash course" || t === "crashcourse";
};

// Union of message ids from lesson list endpoints.
export const mergeLessonIdSet = (
  lists: readonly MessagePrev[][],
): Set<string> => {
  const s = new Set<string>();
  for (const list of lists) {
    for (const m of list) {
      if (m?.message_id) s.add(m.message_id);
    }
  }
  return s;
};

// Lesson ids for badges: API lists + tabbed lesson shape on live messages / reaction cache /
// current panel rows (so cached tab labels from favorites still match).
export const buildLessonIdsForBadges = (
  lessonLists: readonly MessagePrev[][],
  messages: Message[],
  reactedLessonCache: Record<string, MessagePrev>,
  favoriteTabLabelsCache: Record<string, string[]>,
  panelRows: MessagePrev[],
): Set<string> => {
  const s = mergeLessonIdSet(lessonLists);
  const cache = favoriteTabLabelsCache;

  const consider = (m?: MessagePrev | null) => {
    if (!m?.message_id) return;
    if (isLikelyLessonTabbed(m, cache)) s.add(m.message_id);
  };

  for (const m of messages) consider(m);
  for (const m of Object.values(reactedLessonCache)) consider(m);
  for (const m of panelRows) consider(m);

  return s;
};

// Reactions/favorites payloads often omit tabbed_content; reuse live chat message when ids match.
export const withTabbedFromLiveChat = (
  item: MessagePrev,
  messages: Message[],
): MessagePrev => {
  if (item.tabbed_content?.length) return item;
  const live = messages.find((m) => m.message_id === item.message_id);
  if (live?.tabbed_content?.length) {
    return { ...item, tabbed_content: live.tabbed_content };
  }
  return item;
};

export const identifyContentKind = (
  item: MessagePrev,
  { lessonIds, crashCourseIds, favoriteTabLabelsCache }: IdentifyOptions = {},
): ContentKind => {
  if (item.type === "crash_course_form") return "crash_course";
  if (crashCourseIds?.has(item.message_id)) return "crash_course";
  if (isLikelyCrashCourseTitle(item)) return "crash_course";

  if (lessonIds?.has(item.message_id)) return "lesson";
  if (isLikelyLessonTabbed(item, favoriteTabLabelsCache)) return "lesson";

  return "message";
};

export const infoPanelBadgeLabel = (
  kind: ContentKind,
): "Lesson" | "Crash Course" | "Message" => {
  if (kind === "lesson") return "Lesson";
  if (kind === "crash_course") return "Crash Course";
  return "Message";
};
